/**
 * Custom-column cell for `files` columns. The value is a list of
 * `{ id, name, url }` links; the cell shows a paperclip with the count and
 * opens a popover to add or remove entries.
 *
 * Files are referenced by URL rather than uploaded: a tracker cell has no
 * storage of its own, and pointing at where a document already lives keeps a
 * single copy rather than forking one into the board.
 */
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { ExternalLink, Paperclip, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { toHref } from '../../lib/toHref';
import { EM_DASH } from './constants';

/** Persisted shape of one entry in a `files` cell value. */
export interface FileLink {
  id: string;
  name: string;
  url: string;
}

interface FilesCustomCellProps {
  value: FileLink[] | undefined;
  onChange: (next: FileLink[] | null) => void;
  /** Lists the files to open, with no way to add or remove one. */
  readOnly?: boolean;
}

/**
 * One attachment. A URL the browser should not open stays visible as text
 * rather than disappearing — the entry is still there to be removed.
 */
function FileEntry({ file }: { file: FileLink }) {
  const href = toHref(file.url);
  const body = (
    <>
      <ExternalLink className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate">{file.name}</span>
    </>
  );
  const className = 'flex min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 py-1 text-xs';

  if (!href) return <span className={`${className} text-muted-foreground`}>{body}</span>;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={`${className} hover:bg-accent`}
    >
      {body}
    </a>
  );
}

export function FilesCustomCell({ value, onChange, readOnly }: FilesCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');

  const files = value ?? [];

  const addFile = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    const trimmedName = name.trim();
    onChange([...files, { id: uuidv4(), name: trimmedName || trimmedUrl, url: trimmedUrl }]);
    setName('');
    setUrl('');
  };

  const removeFile = (id: string) => {
    const next = files.filter((f) => f.id !== id);
    onChange(next.length === 0 ? null : next);
  };

  if (readOnly && files.length === 0) {
    return (
      <div className="flex h-7 w-full items-center justify-center text-muted-foreground">
        {EM_DASH}
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setName('');
          setUrl('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={
            files.length > 0
              ? t('trackersBoard.filesCustomCell.filesAttached', { count: files.length })
              : t('trackersBoard.filesCustomCell.attachFiles')
          }
          className="flex h-7 w-full items-center justify-center gap-1 rounded text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Paperclip
            className={files.length > 0 ? 'size-3.5 shrink-0' : 'size-4 text-muted-foreground'}
            aria-hidden
          />
          {files.length > 0 ? <span className="tabular-nums">{files.length}</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2" onClick={(e) => e.stopPropagation()}>
        {files.length > 0 ? (
          <ul
            className={`flex max-h-48 flex-col gap-0.5 overflow-y-auto ${readOnly ? '' : 'mb-2'}`}
          >
            {files.map((file) => (
              <li key={file.id} className="flex items-center gap-1">
                <FileEntry file={file} />
                {readOnly ? null : (
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    aria-label={t('trackersBoard.filesCustomCell.removeFile', { name: file.name })}
                    className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {readOnly ? null : (
          <div className="flex flex-col gap-1.5">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addFile();
              }}
              placeholder={t('trackersBoard.filesCustomCell.fileUrl')}
            />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addFile();
              }}
              placeholder={t('trackersBoard.filesCustomCell.displayNameOptional')}
            />
            <Button size="sm" onClick={addFile} disabled={url.trim().length === 0}>
              <Plus className="size-3.5" aria-hidden />
              {t('trackersBoard.filesCustomCell.addFile')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
